import React, { useState, useEffect } from 'react';
import type { Page } from '../../core/types.ts';

interface PageDetailsModalProps {
  pageId: string;
  isOpen: boolean;
  onClose: () => void;
  pageDetails: Record<string, Page>;
  pageDetailsLoading: Record<string, boolean>;
  fetchPageDetails: (id: string) => Promise<Page | null>;
  updatePageDetails: (id: string, fields: Record<string, string | null>) => Promise<Page | null>;
  syncPageDetails: (id: string) => Promise<Page | null>;
}

export const PageDetailsModal: React.FC<PageDetailsModalProps> = ({
  pageId,
  isOpen,
  onClose,
  pageDetails,
  pageDetailsLoading,
  fetchPageDetails,
  updatePageDetails,
  syncPageDetails,
}) => {
  const [localState, setLocalState] = useState<Record<string, string | null>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  const page = pageDetails[pageId];
  const isLoading = pageDetailsLoading[pageId];

  useEffect(() => {
    if (isOpen && pageId && !page) {
      fetchPageDetails(pageId);
    }
  }, [isOpen, pageId, page, fetchPageDetails]);

  useEffect(() => {
    if (page) {
      setLocalState({
        about: page.about ?? null,
        phone: page.phone ?? null,
        email: page.email ?? null,
        website: page.website ?? null,
        hometown: page.hometown ?? null,
        birthday: page.birthday ?? null,
        instagramUsername: page.instagramUsername ?? null,
      });
    }
  }, [page]);

  if (!isOpen) return null;

  const handleChange = (field: string, value: string | null) => {
    setLocalState((prev) => ({ ...prev, [field]: value }));
  };

  const isDirty = (field: string) => {
    if (!page) return false;
    const pageVal = (page as any)[field] ?? null;
    const localVal = localState[field] ?? null;
    return pageVal !== localVal;
  };

  const handleUpdateField = async (field: string) => {
    if (!isDirty(field)) return;
    setIsUpdating(true);
    try {
      await updatePageDetails(pageId, { [field]: localState[field] });
    } catch (err) {
      // Error handled in useAssets
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateAll = async () => {
    const dirtyFields: Record<string, string | null> = {};
    Object.keys(localState).forEach((key) => {
      if (isDirty(key)) {
        dirtyFields[key] = localState[key];
      }
    });
    
    if (Object.keys(dirtyFields).length === 0) return;
    
    setIsUpdating(true);
    try {
      await updatePageDetails(pageId, dirtyFields);
    } catch (err) {
      // Error handled
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await syncPageDetails(pageId);
    } finally {
      setIsSyncing(false);
    }
  };

  const hasAnyDirty = Object.keys(localState).some(isDirty);

  // Generate birthday options
  const bdayOptions = [];
  for (let m = 1; m <= 12; m++) {
    for (let d = 1; d <= 31; d++) {
      bdayOptions.push(`${m.toString().padStart(2, '0')}/${d.toString().padStart(2, '0')}`);
    }
  }

  const renderField = (
    label: string, 
    field: string, 
    type: 'text' | 'textarea' | 'select' = 'text',
    options?: string[]
  ) => {
    const dirty = isDirty(field);
    const val = localState[field] || '';
    
    return (
      <div style={{ marginBottom: 'var(--space-3)' }}>
        <label className="label" style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {label} {dirty && <span style={{ color: 'var(--color-warning)' }} title="Змінено, не збережено">*</span>}
        </label>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          {type === 'textarea' ? (
            <textarea
              className="input"
              rows={3}
              value={val}
              onChange={(e) => handleChange(field, e.target.value)}
              placeholder="—"
              style={{ flex: 1, resize: 'vertical' }}
            />
          ) : type === 'select' ? (
            <select
              className="filter-select"
              value={val}
              onChange={(e) => handleChange(field, e.target.value)}
              style={{ flex: 1 }}
            >
              <option value="">—</option>
              {options?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          ) : (
            <input
              type="text"
              className="input"
              value={val}
              onChange={(e) => handleChange(field, e.target.value)}
              placeholder="—"
              style={{ flex: 1 }}
            />
          )}
          <button
            onClick={() => handleUpdateField(field)}
            disabled={!dirty || isUpdating}
            className="btn btn--secondary"
            style={{ minWidth: '80px' }}
          >
            Оновити
          </button>
        </div>
      </div>
    );
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        backdropFilter: 'blur(4px)',
        padding: 'var(--space-4)'
      }}
      onClick={onClose}
    >
      <div 
        className="glass-card"
        style={{
          width: '100%',
          maxWidth: '650px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-4)', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)' }}>
          <div>
            <h2 style={{ fontSize: '1.2rem', marginBottom: 'var(--space-1)' }}>
              Деталі Fan Page
            </h2>
            {page && (
              <p className="text-sm text-subtle">
                ID: {page.id} • {page.name}
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <button
              onClick={handleUpdateAll}
              disabled={!hasAnyDirty || isUpdating}
              className="btn btn--primary"
            >
              Оновити всі
            </button>
            <button
              onClick={onClose}
              className="btn btn--secondary"
              style={{ padding: '8px' }}
              title="Закрити"
            >
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--space-5)', overflowY: 'auto' }}>
          {isLoading && !page ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
              <div className="skeleton" style={{ width: 32, height: 32, borderRadius: '50%' }} />
            </div>
          ) : !page ? (
            <div className="text-subtle" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>Сторінку не знайдено</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
              
              {/* Sync Status */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--color-surface-1)', padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)' }}>
                <div className="text-sm text-subtle">
                  Останнє оновлення з FB:{' '}
                  <span style={{ color: 'var(--color-text)' }}>
                    {page.detailsSyncedAt ? new Date(page.detailsSyncedAt).toLocaleString() : 'Ніколи'}
                  </span>
                </div>
                <button
                  onClick={handleSync}
                  disabled={isSyncing}
                  className="btn btn--secondary"
                  style={{ fontSize: '0.8rem' }}
                >
                  {isSyncing ? 'Оновлюємо...' : 'Оновити з Facebook'}
                </button>
              </div>

              {/* Info Block */}
              <section>
                <h3 style={{ fontSize: '1.1rem', marginBottom: 'var(--space-3)', color: 'var(--color-primary)' }}>Info</h3>
                <div style={{ marginBottom: 'var(--space-3)' }}>
                  <label className="label">
                    Category
                  </label>
                  <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                    <span className="badge badge--currency">
                      {page.category || '—'}
                    </span>
                    {page.category !== 'Personal blog' && page.category !== 'Личный блог' && page.category !== 'Особистий блог' && (
                      <button
                        onClick={async () => {
                          setIsUpdating(true);
                          try {
                            await updatePageDetails(pageId, { category: '2700' });
                          } finally {
                            setIsUpdating(false);
                          }
                        }}
                        disabled={isUpdating}
                        className="btn btn--secondary"
                        style={{ padding: '2px 8px', fontSize: '0.75rem' }}
                      >
                        Встановити Personal Blog
                      </button>
                    )}
                  </div>
                </div>
                {renderField('About', 'about', 'textarea')}
              </section>

              {/* Contact Block */}
              <section>
                <h3 style={{ fontSize: '1.1rem', marginBottom: 'var(--space-3)', color: 'var(--color-success)' }}>Contact</h3>
                {renderField('Phone', 'phone')}
                {renderField('Email', 'email')}
                {renderField('Website', 'website')}
                {renderField('Instagram Username', 'instagramUsername')}
              </section>

              {/* Personal Details Block */}
              <section>
                <h3 style={{ fontSize: '1.1rem', marginBottom: 'var(--space-3)', color: 'var(--color-warning)' }}>Personal Details</h3>
                {renderField('Hometown', 'hometown')}
                {renderField('Birthday (MM/DD)', 'birthday', 'select', bdayOptions)}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
